import { IconButton } from '@mui/material';
import { VyTooltip } from '../../../../../../../../components/new-design/VyTooltip';
import { ROLES, type Role } from '@vy/types';
import { useQueryClient } from 'react-query';
import { VyDialog } from '../../../../../../../../components/new-design/feedback/VyDialog';
import { useRemoveUser } from '../../../../../../../../hooks/requests/use-remove-user';
import { queryKeys } from '../../../../../../../../services/query-key/query-keys';
import { useDialog } from '../../../../../../../../hooks/use-dialog';
import { ROLE_KEY, USER_ID_KEY } from '../../../../../../../../services/user-auth/user-auth-consts';
import { getLocalStorageString } from '../../../../../../../../utils/local-storage';
import { runMutation } from '../../../../../../../../utils/vy-mutation';
import DeleteIcon from '@mui/icons-material/Delete';

type RemoveUserButtonProps = {
  userId: string;
  userEmail: string;
  userRole: Role;
};

export const RemoveUserButton: React.FC<RemoveUserButtonProps> = ({ userId, userEmail, userRole }) => {
  const { isDialogOpen, handleOpen, handleClose } = useDialog();
  const removeUserMutation = useRemoveUser(userId);
  const queryClient = useQueryClient();

  const currentUserRole = getLocalStorageString(ROLE_KEY);
  const currentUserId = getLocalStorageString(USER_ID_KEY);
  const canRemoveUser = currentUserRole === ROLES.Admin;
  const isProtectedRole = userRole === ROLES.Admin;
  const disabled = !canRemoveUser || (isProtectedRole && currentUserId !== userId);

  const handleRemoveUser = async () => {
    handleClose();
    await runMutation(
      removeUserMutation,
      undefined,
      {
        pending: `Removing ${userEmail}...`,
        success: `User ${userEmail} has been removed`,
        error: `Failed to remove user ${userEmail}`,
      },
      {
        onSuccess: async () => {
          await queryClient.invalidateQueries(queryKeys.users());
        },
      },
    );
  };

  return (
    <>
      <VyTooltip tooltipContent="Remove user">
        <IconButton onClick={handleOpen} size="small" disabled={disabled}>
          <DeleteIcon />
        </IconButton>
      </VyTooltip>

      <VyDialog
        open={isDialogOpen}
        onClose={handleClose}
        label={`Remove ${userEmail}`}
        labelDescription={`You are about to remove '${userEmail}' from Vayu`}
        warningText="Once removed, this user's access to the app will be revoked immediately"
        cancelButtonText="Cancel"
        ctaText="Remove"
        ctaDisabled={removeUserMutation.isLoading}
        onSubmit={handleRemoveUser}
      />
    </>
  );
};
